'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import DateRangePicker from '@/components/DateRangePicker';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAnalytics } from '@/components/Analytics';
import { getPropertyCode, savePropertyCode } from '@/utils/propertyManager';
import { updateLastActivity, getSavedProperty } from '@/utils/sessionManager';

const SearchForm = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { t } = useLanguage();
  const { trackSubmit } = useAnalytics();
  
  const [searchCriteria, setSearchCriteria] = useState({
    checkIn: '',
    checkOut: '',
    adults: '2',
    children: '0',
    room: '1',
    property: ''
  });
  const [isButtonClick, setIsButtonClick] = useState(false);

  // Load initial values from URL parameters or use defaults
  useEffect(() => {
    const checkIn = searchParams.get('checkIn') || formatDate(new Date());
    const checkOut = searchParams.get('checkOut') || formatDate(addDays(new Date(), 1));
    const adults = searchParams.get('adults') || '2';
    const children = searchParams.get('children') || '0';
    const room = searchParams.get('room') || '1';
    
    // Use property manager to get property code with fallback logic
    const property = getPropertyCode() || getSavedProperty();
    
    setSearchCriteria({
      checkIn,
      checkOut,
      adults,
      children,
      room,
      property
    });
    
    // Save property to localStorage if we have one
    if (property) {
      savePropertyCode(property);
    }
  }, [searchParams]);

  // Format date as YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Add days to a date
  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  // Handle date selection
  const handleDateSelect = (startDate, endDate) => {
    // Update session activity on date selection
    updateLastActivity();
    
    const newCheckIn = formatDate(startDate);
    const newCheckOut = formatDate(endDate);
    
    setSearchCriteria(prev => ({
      ...prev,
      checkIn: newCheckIn,
      checkOut: newCheckOut
    }));
    
    // Immediately update URL when dates change
    const propertyToUse = searchCriteria.property || getPropertyCode() || 'PMH';
    const nights = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) || 1;
    
    const queryParams = new URLSearchParams({
      checkIn: newCheckIn,
      checkOut: newCheckOut,
      adults: searchCriteria.adults,
      children: searchCriteria.children,
      room: searchCriteria.room,
      property: propertyToUse
    });
    
    // Preserve roomtype parameter if it exists in current URL
    const currentRoomtype = searchParams.get('roomtype');
    if (currentRoomtype) {
      queryParams.set('roomtype', currentRoomtype);
    }
    
    // Update URL without full page reload
    const newUrl = `/?${queryParams.toString()}`;
    window.history.pushState({}, '', newUrl);
    
    // Trigger content update
    const event = new CustomEvent('search-params-updated', { 
      detail: { 
        queryParams: queryParams.toString(),
        shouldScrollToRooms: false  // Don't scroll when just changing dates
      } 
    });
    window.dispatchEvent(event);
  };

  // Handle input change
  const handleInputChange = (field, value) => {
    // Update session activity on form input changes
    updateLastActivity();
    
    setSearchCriteria(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    
    // Set flag to indicate this is a button click
    setIsButtonClick(true);
    
    // Update session activity on search
    updateLastActivity();
    
    // Ensure we have a valid property code
    const propertyToUse = searchCriteria.property || getPropertyCode() || 'PMH';
    
    // Save property code
    savePropertyCode(propertyToUse);
    
    // Calculate nights
    const startDate = new Date(searchCriteria.checkIn);
    const endDate = new Date(searchCriteria.checkOut);
    const nights = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) || 1;
    
    // Build query string with ensured property
    const queryParams = new URLSearchParams({
      checkIn: searchCriteria.checkIn,
      checkOut: searchCriteria.checkOut,
      adults: searchCriteria.adults,
      children: searchCriteria.children,
      room: searchCriteria.room,
      property: propertyToUse
    });
    
    // Preserve roomtype parameter if it exists in current URL
    const currentRoomtype = searchParams.get('roomtype');
    if (currentRoomtype) {
      queryParams.set('roomtype', currentRoomtype);
    }
    
    // Track search event with analytics
    trackSubmit(`search_rooms_${searchCriteria.property}`);
    
    // Save current scroll position
    const currentScrollPosition = window.scrollY;
    
    // Update URL without causing a full page navigation
    const newUrl = `/?${queryParams.toString()}`;
    window.history.pushState({}, '', newUrl);
    
    // Trigger a soft refresh of the content without full page reload
    // This is needed to update the room data based on new search criteria
    // Include a flag to indicate that scrolling should happen after loading
    const event = new CustomEvent('search-params-updated', { 
      detail: { 
        queryParams: queryParams.toString(),
        shouldScrollToRooms: true  // Add this flag
      } 
    });
    window.dispatchEvent(event);
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-5 sm:p-6 md:p-8">
      <h2 className="text-xl md:text-2xl font-medium text-[#b87d3b] mb-6 tracking-wide">
        Book a Room
      </h2>
      <form onSubmit={handleSearch}>
        <div className="grid grid-cols-12 gap-4 lg:gap-5 items-end">
          {/* Date Picker */}
          <div className="col-span-12 lg:col-span-6 min-w-0">
            <DateRangePicker
              checkIn={searchCriteria.checkIn}
              checkOut={searchCriteria.checkOut}
              onDateSelect={handleDateSelect}
              guests={parseInt(searchCriteria.adults, 10)}
              children={parseInt(searchCriteria.children, 10)}
              roomCount={parseInt(searchCriteria.room, 10)}
              propertyCode={searchCriteria.property}
            />
          </div>

          {/* Adults */}
          <div className="col-span-6 sm:col-span-4 lg:col-span-2">
            <label className="block text-sm text-gray-900 mb-1.5 font-normal whitespace-nowrap">{t.adults}</label>
            <select
              name="adults"
              value={searchCriteria.adults}
              onChange={(e) => handleInputChange('adults', e.target.value)}
              className="w-full px-3 py-3.5 text-base text-gray-700 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-300 bg-white min-h-[48px]"
            >
              {[1, 2, 3, 4, 5, 6].map(num => (
                <option key={`adult-${num}`} value={num}>{num}</option>
              ))}
            </select>
          </div>

          {/* Children */}
          <div className="col-span-6 sm:col-span-4 lg:col-span-2">
            <label className="block text-sm text-gray-900 mb-1.5 font-normal whitespace-nowrap">{t.children}</label>
            <select
              name="children"
              value={searchCriteria.children}
              onChange={(e) => handleInputChange('children', e.target.value)}
              className="w-full px-3 py-3.5 text-base text-gray-700 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-300 bg-white min-h-[48px]"
            >
              {[0, 1, 2, 3, 4].map(num => (
                <option key={`child-${num}`} value={num}>{num}</option>
              ))}
            </select>
          </div>

          {/* Search Button */}
          <div className="col-span-12 sm:col-span-4 lg:col-span-2">
            <button
              type="submit"
              className="w-full py-2.5 bg-[#2ecc71] hover:bg-[#27ae60] text-white rounded-full transition-colors font-medium text-base flex items-center justify-center"
            >
              Search
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SearchForm; 